// ✅ Ruta: src/componentes/Chats.js
import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import { db } from '../firebase';
import {
  collection,
  orderBy,
  startAfter,
  updateDoc,
  setDoc,
  doc,
  onSnapshot,
  serverTimestamp
} from 'firebase/firestore';
import { auth } from '../firebase';
import { useOrganization } from '../context/OrganizationContext';
import { useTranslation } from 'react-i18next';
import { formatRelativeTime } from '../utils/alertHelpers';
import { estadoSeguimientoVenta, ultimaVenta, totalDeVentas } from '../utils/seguimientoVenta';
import {
  citaDestacada, estadoSeguimientoAgenda,
  totalDeReservas, indexarReservasPorChat,
} from '../utils/seguimientoAgenda';
import {
  estadoSeguimientoTraspaso, motivoDeTraspaso, esperaDeTraspaso, traspasoVisibleEnLista,
} from '../utils/seguimientoTraspaso';
import { esperandoRespuesta, diasEsperando, motivoDeEspera } from '../utils/esperaDeRespuesta';
import MarcaDeChat from './inbox/MarcaDeChat';
import { tieneCotizacionEnviada, cotizacionSinRespuesta, fechaDeCotizacion } from '../utils/cotizacionEnviada';
import BannerTopeDelPlan from './inbox/BannerTopeDelPlan';
import { ESTADOS_DE_CHAT, claveDeEtiqueta, configDeEstado, estadoEfectivo } from '../utils/estadosDeChat';
import { admiteBloqueoReal } from '../utils/bloqueoDeContacto';
import { opcionesDeCuenta, hayVariasCuentas, etiquetaDeChat, canalDeCuenta } from '../utils/cuentasDeLaBandeja';
import { cuentaDelChat } from '../utils/cuentaDelChat';
import { useSesionDeBandeja, hayAlgunFiltro, datosDeFiltrosListos } from '../contexto/estadoDeBandeja';
import { decidirAncla, anclaVisible } from './inbox/anclaDeVuelta';

/**
 * CUÁNTOS CHATS SE TRAEN, Y DÓNDE SE DECIDE (9-sep-2026, movido el 24-sep-2026)
 * ────────────────────────────────────────────────────────────────────────────
 * El tamaño de página y el cursor viven ahora en `contexto/datosDeBandeja.js`, junto al
 * oyente: dentro de este componente el oyente se desengancharía al abrir un chat.
 *
 * Lo que queda aquí es CUÁNDO pedir más — el centinela del scroll y el agotado al
 * filtrar— porque eso depende de lo que el operador esté mirando.
 *
 * La nota original, que sigue valiendo: la consulta era `limit(100)` SIN `orderBy` y con
 * la paginación apagada. Con 188 chats, 88 —el 47%— no existían para la pantalla, y los
 * 100 que llegaban eran los primeros por ID de documento, no los más recientes.
 * `actualizadoEn` está presente en los 208 chats de los 18 tenants (medido; control
 * positivo: 46 no tienen `telefono`), así que ordenar por él no esconde ninguno.
 */

/**
 * EL TOPE, Y POR QUÉ EXISTE.
 *
 * Los siete filtros de esta pantalla operan EN MEMORIA sobre lo ya cargado. Con
 * paginación eso significa que filtrar sólo mira la página actual — un resultado a
 * medias que parece completo, que es peor que no filtrar. Por eso, al activar un
 * filtro, se agotan las páginas antes de filtrar.
 *
 * «Son cuatro páginas» es un argumento de hoy, con 188 chats. PUX es multi-tenant: el
 * día que entre un cliente con veinte mil conversaciones, cada clic en un filtro se las
 * descargaría enteras. De ahí el tope.
 *
 * EL NÚMERO EN EL QUE ESTO DEJA DE SERVIR es 2000, y no es arbitrario:
 *   · coste: 2000 lecturas de Firestore por activación de filtro ≈ 0,0012 USD. No es
 *     el dinero lo que manda;
 *   · latencia: 2000/200 = 10 viajes secuenciales ≈ 1,5 s. A 4000 ya es un segundo
 *     más, y se nota al teclear en el buscador;
 *   · pintado: la lista NO está virtualizada. Renderizar 2000 tarjetas ya va justo;
 *     4000 no va.
 *
 * Cuando un tenant real se acerque a 2000, la respuesta NO es subir el tope: es mover
 * los filtros a consultas de Firestore (y eso exige índices compuestos por filtro, más
 * resolver que `busqueda` no es indexable como `contains`). El tope está para que esa
 * decisión llegue por medición y no por una queja.
 */
const TOPE_CARGA_AL_FILTRAR = 2000;

const Chats = () => {
  const { context, loading: orgLoading } = useOrganization();

  /**
   * LOS FILTROS Y LA BÚSQUEDA VIVEN EN EL PROVEEDOR, no aquí.
   *
   * Este componente se DESMONTA al abrir un chat —`/chats` y `/chat/:chatID` son rutas
   * hermanas— así que un `useState` local se destruye en cada viaje. Lo que el operador
   * configuró a mano tiene que sobrevivir; los datos y la paginación no, y por eso se
   * quedan abajo como estado local.
   */
  const {
    filtros, setFiltro, marcarVuelta, consumirAncla, verAncla, limpiarFiltrosDelPanel,
    // La LISTA y su paginación vienen del proveedor: el oyente vive allí para sobrevivir
    // el viaje al detalle. Ver `datosDeBandeja.js`.
    chats, hayMas: hasMore, cargando: loading, oyenteCaido, cargarPagina, generacion, user,
  } = useSesionDeBandeja();
  const {
    estadoFiltro, canal, cuenta, busqueda, dateRange, messageTypeFilter, showUnreadOnly,
    soloRequierenAtencion, soloConVenta, soloConAgenda, soloConTraspaso, soloBloqueados,
    soloConCotizacion, soloCotizacionSinRespuesta,
    showAdvancedFilters,
  } = filtros;
  const setEstadoFiltro = (v) => setFiltro('estadoFiltro', v);
  const setCanal = (v) => setFiltro('canal', v);
  const setCuenta = (v) => setFiltro('cuenta', v);
  const setBusqueda = (v) => setFiltro('busqueda', v);
  const setDateRange = (v) => setFiltro('dateRange', typeof v === 'function' ? v(dateRange) : v);
  const setMessageTypeFilter = (v) => setFiltro('messageTypeFilter', v);
  const setShowUnreadOnly = (v) => setFiltro('showUnreadOnly', v);
  const setSoloRequierenAtencion = (v) => setFiltro('soloRequierenAtencion', v);
  const setSoloConVenta = (v) => setFiltro('soloConVenta', v);
  const setSoloConAgenda = (v) => setFiltro('soloConAgenda', v);
  const setSoloConTraspaso = (v) => setFiltro('soloConTraspaso', v);
  const setSoloBloqueados = (v) => setFiltro('soloBloqueados', v);
  const setSoloConCotizacion = (v) => setFiltro('soloConCotizacion', v);
  const setSoloCotizacionSinRespuesta = (v) => setFiltro('soloCotizacionSinRespuesta', v);
  const setShowAdvancedFilters = (v) => setFiltro('showAdvancedFilters', typeof v === 'function' ? v(showAdvancedFilters) : v);






  // 🔔 feedback temporal cuando se cambia estado
  const [estadoNotif, setEstadoNotif] = useState({});

  // Las cuentas del tenant (`channelAccounts`), para el selector de cuenta y la etiqueta de la
  // tarjeta (commit 10 de multicuenta). Un oyente: cambian al conectar/desconectar un número.
  const [channelAccounts, setChannelAccounts] = useState([]);
  // Las cuentas VISTAS en los chats mientras la bandeja está abierta (revisión del commit 10, 🟠1 y
  // 🟠2): las opciones no pueden salir de `chats`, porque con una cuenta elegida esa lista YA está
  // filtrada y las demás cuentas —y la elegida, si es «desconectada»— desaparecerían del selector.
  // Se acumulan y sólo se vacían al cambiar de tenant.
  const [cuentasVistas, setCuentasVistas] = useState([]);
  // Se enciende cuando el agotado al filtrar chocó con TOPE_CARGA_AL_FILTRAR: a partir
  // de ahí el filtro NO ve todo, y la pantalla tiene que decirlo en vez de mentir.
  const [topeAlcanzado, setTopeAlcanzado] = useState(false);
  // Refs y no estado: los lee la carga en curso, y con estado tendríamos closures
  // caducos y un bucle de re-render con el IntersectionObserver.

  // Espejo de `chats` para que el bucle de agotado lea el largo actual sin entrar en
  // sus dependencias: si `chats` fuera dependencia, cada página cancelaría el bucle.
  const chatsRef = useRef([]);


  const observer = useRef();
  const navigate = useNavigate();
  const { t } = useTranslation('chats');
  const { t: tc } = useTranslation('common');

  // ✨ Fase 4: Advanced Filters




  // Campo PROPIO, no `estado`. `estado` ya tiene dueño semántico: es el embudo de
  // venta (nuevo/contactado/en_proceso/agendado/ganado/perdido/cerrado) y está
  // expuesto en su propio selector acá abajo. Meterle "requiere atención" sería
  // dos significados en un control — y en la práctica obligaría a elegir entre
  // saber en qué etapa está el lead o saber que el bot se cayó, que son las dos
  // cosas que el dueño necesita a la vez.

  // Filtro de la pieza 6. Vive junto al de atención porque son hermanos: uno
  // marca conversaciones que necesitan rescate, el otro las que ya vendieron.

  // Bloqueados: el chat NO se esconde nunca —el historial es del cliente—, pero se puede
  // aislar. Es el filtro que responde "¿a quién estoy ignorando?", que es la pregunta
  // que uno se hace cuando quiere revisar si se pasó de frenada.

  // AGENDA. Hermano de `soloConVenta` en la barra y su opuesto en el almacenamiento:
  // la venta se lee del propio chat, la agenda se calcula de `{tenant}/bookings`. Ver
  // la cabecera de `seguimientoAgenda.js` para por qué esta NO se denormaliza.

  const [reservas, setReservas] = useState([]);
  /**
   * ¿Llegó ya la respuesta de las reservas?
   *
   * NO basta con `reservas.length > 0`: una organización sin ninguna reserva tendría el
   * array vacío para siempre y el filtro de agenda no se estabilizaría nunca. Lo que hay
   * que saber es si la CONSULTA terminó, no si trajo algo.
   */
  const [reservasCargadas, setReservasCargadas] = useState(false);
  // TRASPASO. El tercero de la familia y el único cuyo hecho sólo puede vivir en el
  // chat: no hay coleccion de traspasos de donde derivarlo. Ver `seguimientoTraspaso.js`.



  // `activeNumeroId` se retiró el 23-oct-2026 (commit 10): se consultaba `numeros` al montar y
  // no lo leía nadie. El número de un chat es su cuenta (`cuentaDelChat`), no «el activo».
  useEffect(() => {
    if (!user?.uid || orgLoading) { setChannelAccounts([]); return undefined; }
    const ruta = context?.mode === 'organization' && context?.orgId
      ? collection(db, 'organizations', context.orgId, 'channelAccounts')
      : collection(db, 'clientes', user.uid, 'channelAccounts');
    const parar = onSnapshot(ruta, (snap) => {
      setChannelAccounts(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    }, (error) => {
      console.error('[bandeja] channelAccounts no se pudo leer:', error?.message || error);
      setChannelAccounts([]);
    });
    return () => parar();
  }, [user?.uid, orgLoading, context?.mode, context?.orgId]);
  // Al CAMBIAR de tenant (no al montar: los filtros sobreviven al viaje al detalle), las cuentas
  // vistas se vacían y la elegida vuelve a «todas»: la cuenta de una org no filtra la otra.
  const tenantRef = useRef(null);
  useEffect(() => {
    const clave = `${context?.mode || ''}/${context?.orgId || ''}/${user?.uid || ''}`;
    if (tenantRef.current !== null && tenantRef.current !== clave) {
      setCuentasVistas([]);
      setCuenta('todas');
    }
    tenantRef.current = clave;
  }, [user?.uid, context?.mode, context?.orgId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const nuevas = chats.map((c) => cuentaDelChat(c)).filter(Boolean);
    setCuentasVistas((prev) => {
      const faltan = nuevas.filter((id) => !prev.includes(id));
      return faltan.length ? [...prev, ...faltan] : prev;
    });
  }, [chats]);

  // Los colores e iconos salen de `utils/estadosDeChat`; la etiqueta la resuelve `t()`
  // aquí porque depende del idioma activo y ese módulo no debe llevar texto.
  const etiquetaDeEstado = useCallback(
    (codigo) => t(claveDeEtiqueta(codigo)),
    [t]
  );

  // Helper function to format relative time
  const formatRelativeTime = (timestamp) => {
    if (!timestamp) return t('noDate');

    const now = new Date();
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return t('now');
    if (diffMins < 60) return t('minutesAgo', { count: diffMins });
    if (diffHours < 24) return t('hoursAgo', { count: diffHours });
    if (diffDays === 1) return t('yesterday');
    if (diffDays < 7) return t('daysAgo', { count: diffDays });

    // Format as date if older than 7 days
    return date.toLocaleDateString(undefined, { day: '2-digit', month: 'short' });
  };

  // Configuración de canales con colores oficiales
  const CHANNEL_CONFIG = {
    whatsapp: {
      key: 'whatsapp',
      label: 'WhatsApp',
      icon: '💬',
      color: '#25D366',
      light: '#dcf8c6',
      dark: '#128C7E'
    },
    instagram: {
      key: 'instagram',
      label: 'Instagram',
      icon: '📸',
      color: '#E1306C',
      light: '#fce4ec',
      dark: '#C13584'
    },
    tiktok: {
      key: 'tiktok',
      label: 'TikTok',
      icon: '🎵',
      color: '#000000',
      secondary: '#EE1D52',
      light: '#f5f5f5',
      dark: '#000000'
    },
    email: {
      key: 'email',
      label: 'Email',
      icon: '📧',
      color: '#4285F4',
      light: '#e3f2fd',
      dark: '#1976D2'
    },
    messenger: {
      key: 'messenger',
      label: 'Messenger',
      icon: '💙',
      color: '#0084FF',
      light: '#e3f2fd',
      dark: '#0066CC'
    }
  };

  // Lista de canales para el selector (incluye "Todos")
  const canales = [
    { key: 'todos', label: t('channels.all'), icon: '📱', color: '#6B7280' },
    CHANNEL_CONFIG.whatsapp,
    CHANNEL_CONFIG.instagram,
    CHANNEL_CONFIG.tiktok,
    CHANNEL_CONFIG.email,
    CHANNEL_CONFIG.messenger
  ];


  /**
   * LAS RESERVAS DE LA ORGANIZACIÓN, de una vez.
   *
   * Sin paginar y sin `where`: son 35 documentos en la organización más grande y 90 en
   * las seis juntas (medido el 23-sep-2026). Filtrar por `status` en la consulta habría
   * escondido justo las canceladas, que es lo que distingue `'cancelada'` de
   * `'ninguna'` — y ese es el estado que evita marcar «agendó» a quien anuló.
   *
   * `firestore.rules:362` ya lo permite (`allow read: if isMemberOfOrg(orgId)`), así
   * que esto NO necesita despliegue de reglas.
   *
   * Un fallo aquí NO rompe la bandeja: se registra y la lista queda vacía, o sea sin
   * marcas de agenda. El inbox no puede dejar de funcionar por un adorno.
   */
  useEffect(() => {
    if (!user || orgLoading) return undefined;

    // ⚠️ `onSnapshot` Y NO `getDocs`, desde el 24-sep-2026 — el mismo arreglo que el
    // detalle. Con una foto, cancelar o agendar no cambiaba la píldora de la lista hasta
    // recargar, aunque el dato estuviera bien escrito al segundo.
    //
    // SIN `where`: aquí se necesitan las de TODA la organización, porque la píldora se
    // pinta en cualquier chat de la lista. Son 53 documentos en la organización más
    // grande, y después 1 lectura por reserva que cambie.
    const ref = (context.mode === 'organization' && context.orgId)
      ? collection(db, 'organizations', context.orgId, 'bookings')
      : (user.uid ? collection(db, 'clientes', user.uid, 'bookings') : null);
    if (!ref) return undefined;

    const parar = onSnapshot(
      ref,
      (snap) => {
        setReservas(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setReservasCargadas(true);
      },
      (err) => {
        console.error('[agenda] el oyente de reservas falló:', err?.message || err);
        // ⚠️ `reservasCargadas` SE PONE IGUAL. Un fallo es una respuesta: sin esto el
        // filtro «Solo con agenda» se quedaría esperando para siempre, el esqueleto de
        // carga no se iría nunca y el ancla nunca se descartaría. Es exactamente el
        // defecto que costó tres intentos, por el otro extremo.
        setReservasCargadas(true);
      }
    );

    return () => parar();
  }, [user, orgLoading, context.mode, context.orgId]);

  /**
   * Las reservas, indexadas por chat.
   *
   * ⚠️ EL CENSO DE CHATS SÓLO SE PASA CUANDO ESTÁN TODOS CARGADOS. Con paginación
   * activa, una reserva cuyo chat vive en la página 3 parecería huérfana desde la
   * página 1 — y el aviso diría que faltan chats que sí existen. `indexarReservasPorChat`
   * con `null` no acusa a nadie y las píldoras salen igual; el recuento espera a que
   * `hasMore` sea falso.
   */
  const { porChat: reservasPorChat, huerfanas: reservasHuerfanas } = useMemo(
    () => indexarReservasPorChat(reservas, hasMore ? null : new Set(chats.map((c) => c.id))),
    [reservas, chats, hasMore]
  );

  /**
   * El huérfano, CONTADO y no silenciado.
   *
   * Medido el 23-sep-2026: 1 de los 12 `chatId` con reserva de la organización principal
   * apunta a un chat borrado a mano desde la consola (`CL-1308223934704771`). Sin esta
   * línea ese número no existiría para nadie: el join lo descarta sin ruido.
   *
   * Un `console.warn` es débil —lo ve quien tenga la consola abierta— y se acepta a
   * sabiendas: el contador que dura va en el trabajo del traspaso, que ya toca
   * `functions/` y puede sacarlo como alerta `info` junto a las existentes. Meterlo
   * aquí habría hecho que un commit prometido sin backend tocara backend.
   */
  useEffect(() => {
    if (reservasHuerfanas.length === 0) return;
    console.warn(
      `[agenda] ${reservasHuerfanas.length} reserva(s) apuntan a chats que ya no existen`,
      reservasHuerfanas.map((h) => `${h.chatId} (booking ${h.id})`)
    );
  }, [reservasHuerfanas]);



  /**
   * Abrir un chat, dejando el ancla para la vuelta.
   *
   * El ancla la escribe LA BANDEJA y no el detalle: al detalle se llega desde varios
   * sitios —una notificación, un enlace— y sólo el de aquí significa «volveré a esta
   * lista, a esta altura».
   */
  const irAlChat = (chat) => {
    marcarVuelta(chat.id);
    navigate(`/chat/${chat.id}`, { state: { numeroId: getNumeroIdSeguro(chat) } });
  };

  const filtrarChatsLocal = (lista) => {
    return lista
      // `estadoEfectivo` y no `c.estado`: es la MISMA función que decide qué badge se
      // pinta. Si divergieran, un chat podría mostrar "Sin estado" y no aparecer al
      // filtrar por "Sin estado" — la pantalla contradiciéndose a sí misma.
      .filter(c => estadoFiltro === 'todos' ? true : estadoEfectivo(c) === estadoFiltro)
      .filter(c => canal === 'todos' ? true : (c.canal || 'whatsapp') === canal)
      .filter(
        (chat) =>
          !busqueda.trim() ||
          (chat.nombre && chat.nombre.toLowerCase().includes(busqueda.toLowerCase())) ||
          (chat.ultimoMensaje && chat.ultimoMensaje.toLowerCase().includes(busqueda.toLowerCase())) ||
          (chat.id && chat.id.includes(busqueda)) ||
          (chat.numeroId && chat.numeroId.toLowerCase().includes(busqueda.toLowerCase()))
      )
      // ✨ Fase 4: Date range filter
      .filter(c => {
        if (!dateRange.start && !dateRange.end) return true;
        const chatDate = c.actualizadoEn?.toDate?.() || new Date(0);
        if (dateRange.start && chatDate < new Date(dateRange.start)) return false;
        if (dateRange.end) {
          const endDate = new Date(dateRange.end);
          endDate.setHours(23, 59, 59, 999); // Include full end day
          if (chatDate > endDate) return false;
        }
        return true;
      })
      // ⚠️ «SIN RESPONDER» Y NO «no leídos»: la fuente es `ultimoRemitente`, no un
      // contador. Ver `utils/esperaDeRespuesta.js` — el contador medía si alguien PULSÓ
      // el chat, que es un hecho sobre el operador y no sobre el cliente.
      .filter(c => !showUnreadOnly || esperandoRespuesta(c))
      .filter(c => !soloRequierenAtencion || c.requiereAtencion === true)
      // Mismo criterio que el badge: la función pura decide, el filtro obedece.
      .filter(c => !soloConVenta || estadoSeguimientoVenta(c) !== 'ninguna')
      // Mismo criterio que su píldora, igual que arriba. La diferencia con la venta es
      // de dónde sale el dato, no de cómo se decide.
      .filter(c => !soloConAgenda || estadoSeguimientoAgenda(reservasPorChat.get(c.id)) !== 'ninguna')
      // El filtro usa la MISMA regla que la píldora, no `!== 'ninguno'`. Si no,
      // «Solo con traspaso» devolvería chats sin ninguna marca visible — filtrar por
      // algo y que no se vea es peor que no poder filtrar.
      .filter(c => !soloConTraspaso || traspasoVisibleEnLista(c))
      // F (3-nov-2026): la cotización entregada; «sin respuesta» = el cliente no escribió después.
      .filter(c => !soloConCotizacion || tieneCotizacionEnviada(c))
      .filter(c => !soloCotizacionSinRespuesta || cotizacionSinRespuesta(c))
      .filter(c => !soloBloqueados || c.contactoBloqueado === true)
      // Con el filtro de atención activo manda la ANTIGÜEDAD de la marca, no la
      // última actividad: el que lleva más esperando va arriba. Un lead que
      // quedó colgado hace 20 minutos sigue caliente; uno de hace dos días ya
      // se fue a la competencia, y ordenar por actividad reciente lo esconde
      // justo cuando es más urgente. Sin filtro, el orden habitual.
      .sort((a,b) => soloRequierenAtencion
        ? (a.atencionDesde?.toMillis?.() || 0) - (b.atencionDesde?.toMillis?.() || 0)
        : (b.actualizadoEn?.toMillis?.() || 0) - (a.actualizadoEn?.toMillis?.() || 0));
  };

  // Calcular contadores por canal (useMemo para optimizar)
  const channelCounts = useMemo(() => {
    const counts = {
      todos: chats.length,
      whatsapp: 0,
      instagram: 0,
      tiktok: 0,
      email: 0,
      messenger: 0
    };

    chats.forEach(chat => {
      const chatCanal = chat.canal || 'whatsapp'; // default to whatsapp for legacy chats
      if (counts.hasOwnProperty(chatCanal)) {
        counts[chatCanal]++;
      }
    });

    return counts;
  }, [chats]);

  // Las cuentas del canal elegido: `channelAccounts` ∪ las vistas en los chats sin doc
  // («desconectada»). Salen de `cuentasVistas`, NUNCA de `chats` (ya filtrada por cuenta). El
  // selector existe con más de una cuenta (`hayVariasCuentas`) o mientras haya una elegida —si
  // no, con el filtro puesto podría esconderse y dejar al operador sin forma de quitarlo—. Con
  // una sola cuenta y nada elegido, la pantalla es la de siempre. `todasLasOpciones` alimenta
  // la etiqueta de la tarjeta.
  const chatsVistos = useMemo(() => cuentasVistas.map((accountId) => ({ accountId })), [cuentasVistas]);
  const todasLasOpciones = useMemo(() => opcionesDeCuenta({ chats: chatsVistos, channelAccounts, canal: 'todos' }), [chatsVistos, channelAccounts]);
  const opcionesDelCanal = useMemo(() => {
    const base = opcionesDeCuenta({ chats: chatsVistos, channelAccounts, canal });
    // La elegida siempre está en la lista (aunque todavía no se haya visto ningún chat suyo),
    // para que el <select> muestre lo que de verdad filtra.
    if (cuenta !== 'todas' && !base.some((o) => o.accountId === cuenta)) {
      const c = canalDeCuenta(cuenta) || 'whatsapp';
      base.push({ accountId: cuenta, canal: c, principal: cuenta.slice(c.length + 1), secundaria: null, desconectada: true });
    }
    return base;
  }, [chatsVistos, channelAccounts, canal, cuenta]);
  const mostrarSelectorDeCuenta = cuenta !== 'todas' || hayVariasCuentas(canal, opcionesDelCanal);
  // La elegida sólo se suelta al cambiar a un CANAL distinto del suyo (una cuenta de WhatsApp no
  // filtra Instagram). Nunca por «ya no la veo»: la lista filtrada no es quien decide.
  useEffect(() => {
    if (cuenta !== 'todas' && canal !== 'todos' && canalDeCuenta(cuenta) !== canal) setCuenta('todas');
  }, [cuenta, canal]); // eslint-disable-line react-hooks/exhaustive-deps

  const coleccionDeChats = useCallback(() => {
    if (context.mode === 'organization' && context.orgId) {
      return collection(db, 'organizations', context.orgId, 'chats');
    }
    return collection(db, 'clientes', user.uid, 'chats');
  }, [context.mode, context.orgId, user]);


  /**
 * LA CARGA VIVE EN EL PROVEEDOR.
 *
 * `cargarPagina`, el cursor y el oyente se fueron a `datosDeBandeja.js`: dentro de este
 * componente el oyente se desengancharía al abrir un chat y se reengancharía al volver,
 * que es exactamente el gasto que este trabajo elimina.
 *
 * Aquí sólo queda CUÁNDO pedir más — el centinela del scroll y el agotado al filtrar.
 */

  // El espejo del largo, para que el bucle de agotado no meta `chats` en dependencias.
  chatsRef.current = chats;

  // La primera página ya NO se pide aquí: la trae el oyente del proveedor. Cambiar de
  // organización tampoco se limpia aquí — lo hace el propio oyente al reengancharse,
  // que es el único que sabe cuándo la ruta cambió de verdad.
  useEffect(() => { setTopeAlcanzado(false); }, [context.mode, context.orgId]);

  const loaderRef = useRef();

  // ── AGOTAR ANTES DE FILTRAR ────────────────────────────────────────────────
  // Los siete filtros de `filtrarChatsLocal` miran sólo lo cargado. Con cualquiera
  // activo hay que traerse el resto ANTES, o el resultado sale a medias con pinta de
  // completo. Se para en TOPE_CARGA_AL_FILTRAR y se enciende el aviso: mejor decir
  // "esto no lo he mirado todo" que enseñar una lista corta como si fuera la buena.
  // También DERIVADO: enumerarlo aquí era la otra mitad del mismo defecto. Un filtro
  // nuevo que no entrara en esta lista no agotaría páginas, y el resultado saldría a
  // medias con pinta de completo — que es peor que no filtrar.
  const hayFiltroActivo = hayAlgunFiltro(filtros);

  /**
   * VOLVER A LA ALTURA DEL CHAT DEL QUE SE SALIÓ.
   *
   * La decisión —ir, esperar o descartar— vive en `anclaDeVuelta.js`, fuera de este
   * componente, porque aquí no se puede probar: hicieron falta DOS arreglos fallidos para
   * aprenderlo. Allí está razonado qué falló cada vez.
   *
   * ⚠️ SIN `requestAnimationFrame`. La versión anterior programaba un fotograma y su
   * limpieza lo cancelaba; con un filtro activo la lista cambia varias veces seguidas
   * —el agotado de páginas— así que cada pasada mataba el fotograma de la anterior.
   * `useEffect` ya corre con el DOM confirmado: el nodo existe cuando se busca.
   *
   * ⚠️ LA DEPENDENCIA ES DE CONTENIDO, no de cantidad. Era `listaVisible.length`, y con
   * un filtro activo una página nueva puede añadir CERO coincidencias: la longitud no
   * cambiaba y el reintento no ocurría nunca.
   */
  const listaVisible = filtrarChatsLocal(chats);
  /**
   * ¿La lista ya no va a crecer?
   *
   * Es el punto final del ancla: sin él, un chat que no está en lo cargado la dejaría
   * viva para siempre y saltaría en un momento que el operador ya no espera.
   */
  const datosListos = datosDeFiltrosListos(filtros, { reservasCargadas });
  const listaEstable = !loading && datosListos && (!hayFiltroActivo || !hasMore || topeAlcanzado);

  const idsVisibles = listaVisible.map((c) => c.id);
  const anclaActual = verAncla();
  const estaVisible = anclaVisible(anclaActual, idsVisibles);

  useEffect(() => {
    const id = verAncla();
    const decision = decidirAncla({
      ancla: id, visibleIds: idsVisibles, listaEstable, datosDeFiltroListos: datosListos,
    });
    if (decision === 'sin-ancla' || decision === 'esperar') return;

    if (decision === 'ir') {
      const nodo = document.querySelector(`[data-chat-id="${CSS.escape(id)}"]`);
      if (nodo) nodo.scrollIntoView({ block: 'center' });
    }
    consumirAncla();
    // Las TRES cosas que pueden cambiar la decisión. `datosListos` es la que faltaba.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estaVisible, listaEstable, datosListos]);

  useEffect(() => {
    if (!hayFiltroActivo) return;
    let cancelado = false;

    (async () => {
      while (!cancelado && hasMore) {
        if (chatsRef.current.length >= TOPE_CARGA_AL_FILTRAR) {
          setTopeAlcanzado(true);
          return;
        }
        const traidos = await cargarPagina();
        // `null` = página descartada porque la cuenta cambió en vuelo: este bucle ya está
        // cancelado o lo estará; el nuevo arranca con `generacion` (abajo, en las dependencias).
        if (traidos === 0 || traidos === null) return;
      }
      setTopeAlcanzado(false);
    })();

    return () => { cancelado = true; };
  }, [hayFiltroActivo, cargarPagina, generacion]);

  useEffect(() => {
    const options = { root: null, rootMargin: '20px', threshold: 1.0 };

    observer.current = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        cargarPagina();
      }
    }, options);

    if (loaderRef.current) {
      observer.current.observe(loaderRef.current);
    }

    return () => {
      if (observer.current && loaderRef.current) {
        observer.current.unobserve(loaderRef.current);
      }
    };
  }, [cargarPagina]);

  // ✅ Cambiar estado (optimista) usando estructura unificada
  async function handleEstadoChange(chatId, newEstado) {
    if (!user?.uid) return;

    // ⚠️ AQUÍ HABÍA UNA ACTUALIZACIÓN OPTIMISTA, y sobra desde que la lista es viva.
    // Firestore aplica la escritura en local y el `onSnapshot` dispara al instante
    // (`metadata.hasPendingWrites`), así que el operador ve el cambio igual de rápido —
    // y si la escritura falla, el SDK revierte solo. Mantenerla obligaría a que el
    // proveedor expusiera un `setChats` que el oyente pisaría a los pocos milisegundos.
    // 2) Feedback temporal sin ensuciar 'ultimoMensaje'
    setEstadoNotif(prev => ({ ...prev, [chatId]: `Estado cambiado a ${newEstado}` }));
    setTimeout(() => setEstadoNotif(prev => ({ ...prev, [chatId]: null })), 3000);
    // 3) Persistir en estructura unificada
    try {
      // Update chat in correct location based on org context
      let chatRef;
      if (context.mode === 'organization' && context.orgId) {
        chatRef = doc(db, 'organizations', context.orgId, 'chats', chatId);
      } else {
        chatRef = doc(db, 'clientes', user.uid, 'chats', chatId);
      }
      const ref = chatRef;
      await updateDoc(ref, { estado: newEstado, lastTs: serverTimestamp(), updatedAt: serverTimestamp() });
    } catch (err) {
      console.error('Error actualizando estado:', err);
    }
  }

  // Human takeover: pause/resume bot for a single conversation. Optimistic UI
  // update + setDoc with merge (chat doc always exists when listed, but merge
  // keeps it safe against a stale snapshot).
  async function toggleBotPauseChat(chatId, currentlyPaused) {
    if (!user?.uid) return;
    // Sin actualización optimista: la escritura local ya dispara el oyente. Ver la nota
    // de `handleEstadoChange`.
    try {
      let chatRef;
      if (context.mode === 'organization' && context.orgId) {
        chatRef = doc(db, 'organizations', context.orgId, 'chats', chatId);
      } else {
        chatRef = doc(db, 'clientes', user.uid, 'chats', chatId);
      }
      await setDoc(chatRef, {
        botPaused: !currentlyPaused,
        botPausedAt: currentlyPaused ? null : serverTimestamp(),
        botPausedBy: currentlyPaused ? null : user.uid,
        // `botResumedAutomatically` NO se escribe: ver CLAUDE.md, "La pausa de un
        // chat no caduca". Ponerlo a `false` aquí borraría, en la siguiente pausa
        // manual, el único rastro que queda de las reanudaciones automáticas.
      }, { merge: true });
    } catch (err) {
      // Sin rollback a mano: el SDK revierte la escritura local fallida y el oyente
      // vuelve a emitir el valor bueno.
      console.error('[BotPause] toggle error:', err);
    }
  }

  const exportarChats = async (formato) => {
    try {
      if (!user?.uid) return alert("No autenticado");

      const response = await fetch(
        `https://us-central1-sistema-bks.cloudfunctions.net/exportarChats?clienteID=${user.uid}&formato=${formato}`
      );

      if (!response.ok) throw new Error("Error al exportar");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `chats.${formato}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (err) {
      console.error("Error exportando:", err);
      alert("No se pudo exportar los chats.");
    }
  };

  const exportarMensajes = async (chatID, formato) => {
    try {
      if (!user?.uid) return alert("No autenticado");

      const response = await fetch(
        `https://us-central1-sistema-bks.cloudfunctions.net/exportarMensajes?clienteID=${user.uid}&chatID=${chatID}&formato=${formato}`
      );

      if (!response.ok) throw new Error("Error al exportar mensajes");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `mensajes_${chatID}.${formato}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (err) {
      console.error("Error exportando mensajes:", err);
      alert("No se pudo exportar los mensajes.");
    }
  };

  const getNumeroIdSeguro = (chat) => {
    if (chat.numeroId) return chat.numeroId;
    if (chat.id.includes('_')) return chat.id.split('_')[0];
    return 'default';
  };

  // ✨ Fase 5: Skeleton Loader Component
  const SkeletonChatCard = () => (
    <div style={{
      ...styles.chatCard,
      animation: 'pulse 1.5s ease-in-out infinite'
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        marginBottom: '0.75rem'
      }}>
        <div style={{ flex: 1 }}>
          <div style={{
            height: '16px',
            backgroundColor: '#e2e8f0',
            borderRadius: '4px',
            width: '60%',
            marginBottom: '0.5rem'
          }} />
          <div style={{
            height: '12px',
            backgroundColor: '#e2e8f0',
            borderRadius: '4px',
            width: '40%'
          }} />
        </div>
      </div>
      <div style={{
        height: '40px',
        backgroundColor: '#e2e8f0',
        borderRadius: '4px',
        marginBottom: '0.5rem'
      }} />
      <div style={{
        height: '32px',
        backgroundColor: '#e2e8f0',
        borderRadius: '4px'
      }} />
    </div>
  );

  // Guard temprano para auth
  if (!user) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <div style={{
          display: 'inline-block',
          animation: 'spin 1s linear infinite',
          fontSize: '32px'
        }}>
          🔄
        </div>
        <p style={{ marginTop: '1rem', color: '#6c757d' }}>{tc('initializing')}</p>
      </div>
    );
  }

  return (
    <div style={{ padding: 'clamp(12px, 3vw, 32px)', maxWidth: '900px', margin: '0 auto', width: '100%', boxSizing: 'border-box' }}>
      <PageHeader title={t('title')} subtitle={t('subtitle')} />
      <BannerTopeDelPlan context={context} />

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
        <button
          onClick={() => navigate('/chats-export')}
          style={{
            padding: '0.5rem 1rem',
            backgroundColor: '#4299e1',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            fontSize: '13px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            whiteSpace: 'nowrap',
          }}
        >
          {t('export.exportButton')}
        </button>
      </div>

      {/* Channel Selector - Pill Style Buttons */}
      <div style={{
        marginBottom: '1.5rem',
        display: 'flex',
        flexWrap: 'nowrap',
        gap: '0.75rem',
        padding: '1rem',
        backgroundColor: '#f8f9fa',
        borderRadius: '12px',
        border: '1px solid #e9ecef',
        overflowX: 'auto',
        WebkitOverflowScrolling: 'touch',
        scrollbarWidth: 'thin',
        maskImage: 'linear-gradient(to right, black 85%, transparent 100%)',
        WebkitMaskImage: 'linear-gradient(to right, black 85%, transparent 100%)',
      }}>
        {canales.map((canalOption) => {
          const isActive = canal === canalOption.key;
          const count = channelCounts[canalOption.key] || 0;
          const channelColor = canalOption.color || '#6B7280';

          return (
            <button
              key={canalOption.key}
              onClick={() => setCanal(canalOption.key)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.625rem 1rem',
                backgroundColor: isActive ? channelColor : '#ffffff',
                color: isActive ? '#ffffff' : '#4a5568',
                border: isActive ? 'none' : '1px solid #cbd5e0',
                borderRadius: '20px',
                fontSize: '14px',
                fontWeight: isActive ? '600' : '500',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: isActive ? '0 2px 8px rgba(0, 0, 0, 0.15)' : '0 1px 3px rgba(0, 0, 0, 0.05)',
                transform: isActive ? 'translateY(-1px)' : 'none',
                flexShrink: 0,
                whiteSpace: 'nowrap',
              }}
              onMouseEnter={(e) => {
                if (!isActive) {
                  e.currentTarget.style.backgroundColor = '#f1f3f5';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 2px 6px rgba(0, 0, 0, 0.1)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isActive) {
                  e.currentTarget.style.backgroundColor = '#ffffff';
                  e.currentTarget.style.transform = 'none';
                  e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.05)';
                }
              }}
            >
              <span style={{ fontSize: '18px' }}>{canalOption.icon}</span>
              <span>{canalOption.label}</span>
              <span style={{
                backgroundColor: isActive ? 'rgba(255, 255, 255, 0.3)' : '#e9ecef',
                color: isActive ? '#ffffff' : '#6B7280',
                padding: '0.125rem 0.5rem',
                borderRadius: '10px',
                fontSize: '12px',
                fontWeight: '600',
                minWidth: '24px',
                textAlign: 'center'
              }}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* ✨ Fase 4: Advanced Filters Panel */}
      <div style={{
        marginBottom: '1rem',
        border: '1px solid #e9ecef',
        borderRadius: '8px',
        overflow: 'hidden',
        backgroundColor: '#ffffff'
      }}>
        <button
          onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
          style={{
            width: '100%',
            padding: '0.75rem 1rem',
            backgroundColor: '#f8f9fa',
            border: 'none',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            cursor: 'pointer',
            fontWeight: '500',
            fontSize: '14px',
            color: '#495057',
            transition: 'background-color 0.2s'
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e9ecef'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
        >
          <span>{t('filters.advancedFilters')}</span>
          <span style={{ fontSize: '12px' }}>{showAdvancedFilters ? '▲' : '▼'}</span>
        </button>

        {showAdvancedFilters && (
          <div style={{
            padding: '1rem',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '1rem',
            borderTop: '1px solid #e9ecef',
            animation: 'slideDown 0.2s ease-out'
          }}>
            {/* Date Range */}
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#6c757d', marginBottom: '0.25rem' }}>
                {t('filters.dateFrom')}
              </label>
              <input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid #cbd5e0',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#6c757d', marginBottom: '0.25rem' }}>
                {t('filters.dateTo')}
              </label>
              <input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid #cbd5e0',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
            </div>

            {/* Unread Only Toggle */}
            <div>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                color: '#495057',
                padding: '0.5rem',
                borderRadius: '6px',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <input
                  type="checkbox"
                  checked={showUnreadOnly}
                  onChange={(e) => setShowUnreadOnly(e.target.checked)}
                  style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                />
                <span>{t('filters.unansweredOnly')}</span>
              </label>

              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                color: '#495057',
                padding: '0.5rem',
                borderRadius: '6px',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <input
                  type="checkbox"
                  checked={soloRequierenAtencion}
                  onChange={(e) => setSoloRequierenAtencion(e.target.checked)}
                  style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                />
                <span>{t('filters.needsAttentionOnly')}</span>
              </label>

              <label style={{
                  display: 'flex', alignItems: 'center', gap: '0.5rem',
                  fontSize: '14px', color: '#4a5568', cursor: 'pointer',
                  padding: '0.4rem', borderRadius: '6px'
              }}>
                <input
                  type="checkbox"
                  checked={soloConVenta}
                  onChange={(e) => setSoloConVenta(e.target.checked)}
                  style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                />
                <span>Solo con venta</span>
              </label>

              <label style={{
                  display: 'flex', alignItems: 'center', gap: '0.5rem',
                  fontSize: '14px', color: '#4a5568', cursor: 'pointer',
                  padding: '0.4rem', borderRadius: '6px'
              }}>
                <input
                  type="checkbox"
                  checked={soloConAgenda}
                  onChange={(e) => setSoloConAgenda(e.target.checked)}
                  style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                />
                <span>Solo con agenda</span>
              </label>

              <label style={{
                  display: 'flex', alignItems: 'center', gap: '0.5rem',
                  fontSize: '14px', color: '#4a5568', cursor: 'pointer',
                  padding: '0.4rem', borderRadius: '6px'
              }}>
                <input
                  type="checkbox"
                  checked={soloConTraspaso}
                  onChange={(e) => setSoloConTraspaso(e.target.checked)}
                  style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                />
                <span>Solo con traspaso</span>
              </label>

              {/* Ojo: la etiqueta de arriba es un literal en español, preexistente. La
                  de abajo va por i18n, que es lo que corresponde. No se arregla la
                  vecina aquí para no mezclar. */}
              <label style={{
                  display: 'flex', alignItems: 'center', gap: '0.5rem',
                  fontSize: '14px', color: '#4a5568', cursor: 'pointer',
                  padding: '0.4rem', borderRadius: '6px'
              }}>
                <input
                  type="checkbox"
                  checked={soloBloqueados}
                  onChange={(e) => setSoloBloqueados(e.target.checked)}
                  style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                />
                <span>{t('block.filterOnly')}</span>
              </label>

              {/* F (3-nov-2026): la cotización entregada (`utils/cotizacionEnviada.js`). */}
              <label style={{
                  display: 'flex', alignItems: 'center', gap: '0.5rem',
                  fontSize: '14px', color: '#4a5568', cursor: 'pointer',
                  padding: '0.4rem', borderRadius: '6px'
              }}>
                <input
                  type="checkbox"
                  checked={soloConCotizacion}
                  onChange={(e) => setSoloConCotizacion(e.target.checked)}
                  style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                />
                <span>{t('cotizacion.filterOnly')}</span>
              </label>
              <label style={{
                  display: 'flex', alignItems: 'center', gap: '0.5rem',
                  fontSize: '14px', color: '#4a5568', cursor: 'pointer',
                  padding: '0.4rem', borderRadius: '6px'
              }}>
                <input
                  type="checkbox"
                  checked={soloCotizacionSinRespuesta}
                  onChange={(e) => setSoloCotizacionSinRespuesta(e.target.checked)}
                  style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                />
                <span>{t('cotizacion.filterSinRespuesta')}</span>
              </label>
            </div>

            {/* Clear Filters Button */}
            <div style={{ display: 'flex', alignItems: 'flex-end' }}>
              <button
                // ⚠️ SE DERIVA DEL ESTADO INICIAL, no de una lista escrita aquí. Antes
                // enumeraba seis setters a mano y se quedó sin limpiar `soloConAgenda`
                // ni `soloConTraspaso` — los dos últimos que se añadieron, que es
                // exactamente el fallo que se repite con cada marcador nuevo.
                // Ver `limpiarFiltrosDelPanel` en `estadoDeBandeja.js`.
                onClick={limpiarFiltrosDelPanel}
                style={{
                  width: '100%',
                  padding: '0.5rem 1rem',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#5a6268'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#6c757d'}
              >
                {t('filters.clearFilters')}
              </button>
            </div>
          </div>
        )}
      </div>

      <div style={{ marginBottom: '1rem', display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}>
        <select
          value={estadoFiltro}
          onChange={(e) => setEstadoFiltro(e.target.value)}
          style={{ padding: '0.5rem', flex: '1 1 180px', minWidth: 0, borderRadius: '6px', border: '1px solid #cbd5e0' }}
          aria-label={t('filters.filterByStatus')}
        >
          <option value="todos">{t('filters.allStatuses')}</option>
          {/* Los OCHO, recorriendo la fuente única. Antes había cuatro a mano y dejaban
              fuera 3 de los 5 estados en uso: 2 chats en `agendado` y 1 en `perdido` no
              se podían encontrar por el filtro que los clasificaba. */}
          {ESTADOS_DE_CHAT.map(({ codigo }) => (
            <option key={codigo} value={codigo}>{etiquetaDeEstado(codigo)}</option>
          ))}
        </select>

        {mostrarSelectorDeCuenta && (
          <select
            value={cuenta}
            onChange={(e) => setCuenta(e.target.value)}
            style={{ padding: '0.5rem', flex: '1 1 180px', minWidth: 0, borderRadius: '6px', border: '1px solid #cbd5e0' }}
            aria-label={t('filters.filterByAccount')}
            data-testid="selector-de-cuenta"
          >
            <option value="todas">{t('filters.allAccounts')}</option>
            {opcionesDelCanal.map((o) => (
              <option key={o.accountId} value={o.accountId}>
                {canal === 'todos' ? `${CHANNEL_CONFIG[o.canal]?.label || o.canal} · ` : ''}{o.principal}{o.secundaria ? ` · ${o.secundaria}` : ''}{o.desconectada ? ` · ${t('account.disconnected')}` : ''}
              </option>
            ))}
          </select>
        )}

        <input
          type="text"
          placeholder={t('filters.searchPlaceholder')}
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          style={{ padding: '0.5rem', flex: '1 1 220px', minWidth: 0, borderRadius: '6px', border: '1px solid #cbd5e0' }}
          aria-label={t('filters.searchChats')}
        />

      </div>

      {/* ✨ Fase 5: Loading State with Skeleton Loaders */}
      {/* ⚠️ `!datosListos` TAMBIÉN es cargar, y es la mitad VISIBLE del defecto del
          24-sep. Con «Solo con agenda» puesto, el oyente entrega los 297 chats en
          milisegundos y las reservas —un `getDocs` aparte— tardan más: en esa ventana
          NINGÚN chat cumple el filtro. Sin esta condición la bandeja anunciaba «no hay
          chats» y se corregía sola un instante después. Decir «no hay» de algo que
          todavía no se ha mirado es peor que tardar en decirlo. */}
      {(loading && chats.length === 0) || !datosListos ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }} aria-label={tc('loading')}>
          {[1, 2, 3].map(i => <SkeletonChatCard key={i} />)}
        </div>
      ) : listaVisible.length === 0 ? (
        <div style={{
          padding: '3rem 2rem',
          textAlign: 'center',
          backgroundColor: '#f8f9fa',
          borderRadius: '12px',
          border: '2px dashed #dee2e6'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '1rem' }}>📭</div>
          <p style={{ fontSize: '18px', fontWeight: '500', color: '#6c757d', margin: 0 }}>
            {t('list.empty')}
          </p>
          <p style={{ fontSize: '14px', color: '#adb5bd', marginTop: '0.5rem' }}>
            {t('list.emptyHint')}
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }} role="list" aria-label="Lista de chats">
          {listaVisible.map((chat) => {
            // Get channel and status config
            const chatCanal = chat.canal || 'whatsapp';
            const channelConfig = CHANNEL_CONFIG[chatCanal] || CHANNEL_CONFIG.whatsapp;
            const chatEstado = estadoEfectivo(chat);
            const cfgEstado = configDeEstado(chatEstado);
            const statusConfig = {
              color: cfgEstado.color,
              icon: cfgEstado.icono,
              label: etiquetaDeEstado(chatEstado),
            };

            return (
            <div
              key={chat.id}
              // El ancla de vuelta busca por aquí. `key` no sirve: React no lo pone en
              // el DOM, así que un `querySelector` no lo vería nunca.
              data-chat-id={chat.id}
              role="listitem"
              tabIndex={0}
              aria-label={`Chat con ${chat.nombre || chat.remitente || t('list.noName')}, canal ${channelConfig.label}, estado ${statusConfig.label}`}
              style={{
                ...styles.chatCard,
                borderLeft: `4px solid ${channelConfig.color}`,
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onClick={() => irAlChat(chat)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  irAlChat(chat);
                }
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.12)';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              {/* Bandera de atención: el bot se rindió en esta conversación */}
              {chat.requiereAtencion === true && (
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.4rem',
                  marginBottom: '0.6rem',
                  padding: '0.25rem 0.6rem',
                  borderRadius: '999px',
                  backgroundColor: '#fed7d7',
                  color: '#822727',
                  fontSize: '12px',
                  fontWeight: 600
                }}>
                  ⚠️ {t(`attention.motivos.${chat.atencionMotivo}`, {
                    defaultValue: t('attention.badge')
                  })}
                  {chat.atencionDesde?.toDate && (
                    <span style={{ fontWeight: 500, opacity: 0.85 }}>
                      · {formatRelativeTime(chat.atencionDesde.toDate(), t)}
                    </span>
                  )}
                </div>
              )}

              {/* La CUENTA del chat, sólo cuando su canal tiene más de una en la org (commit 10).
                  Con un número, nada que distinguir y nada que pintar. */}
              {(() => {
                const etiqueta = etiquetaDeChat(chat, todasLasOpciones);
                if (!etiqueta) return null;
                return (
                  <MarcaDeChat
                    icono={channelConfig.icon}
                    texto={etiqueta.principal}
                    sufijo={etiqueta.desconectada ? `· ${t('account.disconnected')}` : null}
                    fondo={etiqueta.desconectada ? '#e2e8f0' : channelConfig.light || '#edf2f7'}
                    titulo={etiqueta.accountId}
                  />
                );
              })()}

              {/* Marca de VENTA. El estado lo decide una sola función pura
                  (`estadoSeguimientoVenta`), la misma que usan el banner del detalle y el
                  filtro de arriba. Si cada uno lo calculara por su cuenta reproduciríamos
                  el patrón de los dos lectores del ancla pux_*, ya anotado como deuda.

                  "vencida" NO se oculta: sigue visible hasta que el usuario la elimine. */}
              {(() => {
                const estadoVenta = estadoSeguimientoVenta(chat);
                if (estadoVenta === 'ninguna') return null;
                const venta = ultimaVenta(chat);
                const totalCompras = totalDeVentas(chat);
                const vencida = estadoVenta === 'vencida';
                return (
                  <MarcaDeChat
                    icono={vencida ? '💤' : '💰'}
                    texto={venta?.orderNumber || 'Venta'}
                    sufijo={totalCompras > 1 ? `· ${totalCompras} compras` : null}
                    fondo={vencida ? '#e2e8f0' : '#c6f6d5'}
                    color={vencida ? '#4a5568' : '#22543d'}
                  />
                );
              })()}

              {/* Marca de AGENDA. Misma función pura que el banner del detalle y el
                  filtro de arriba, igual que su hermana de venta — pero el dato NO sale
                  del chat: sale del join de `{tenant}/bookings` por `chatId`. Ver la
                  cabecera de `seguimientoAgenda.js`.

                  "pasada" y "cancelada" NO se ocultan: que una cita ya ocurriera o se
                  anulara es información del operador, y esconderla haría que el chat
                  pareciera no haber agendado nunca. */}
              {(() => {
                const delChat = reservasPorChat.get(chat.id);
                // Una sola llamada — la misma que el banner del detalle. Ver ahí por qué.
                const { estado: estadoAgenda, cita } = citaDestacada(delChat);
                if (estadoAgenda === 'ninguna') return null;
                const total = totalDeReservas(delChat);
                const cuando = cita ? `${cita.date || ''}${cita.time ? ` ${cita.time}` : ''}`.trim() : '';
                const porEstado = {
                  proxima:   { icono: '📅', fondo: '#bee3f8', color: '#1a365d', texto: cuando || 'Cita' },
                  pasada:    { icono: '🗓️', fondo: '#e2e8f0', color: '#4a5568', texto: cuando || 'Cita pasada' },
                  // `cancelada` ya trae su cita —antes `cita` era null en esta rama y la
                  // píldora decía «Cita cancelada» a secas—, así que se ve CUÁL se canceló.
                  cancelada: { icono: '🚫', fondo: '#fed7d7', color: '#822727', texto: cuando || 'Cita cancelada' },
                }[estadoAgenda];
                return (
                  <MarcaDeChat
                    icono={porEstado.icono}
                    texto={porEstado.texto}
                    sufijo={total > 1 ? `· ${total} citas` : null}
                    fondo={porEstado.fondo}
                    color={porEstado.color}
                    titulo={cita?.title || undefined}
                  />
                );
              })()}

              {/* Marca de TRASPASO. El hecho es la llamada a `solicitarTraspaso`, nunca
                  una frase del modelo — ver la cabecera de `seguimientoTraspaso.js`.

                  «atendido» NO se oculta: que a esta conversación se le prometiera una
                  persona, y cuánto tardó en llegar, es información del operador. Lo que
                  desaparece al atenderla es la ALERTA del Dashboard, no la marca. */}
              {(() => {
                // ⚠️ `traspasoVisibleEnLista` y no `!== 'ninguno'`: un traspaso ATENDIDO
                // deja la bandeja a las 12 h. El hecho no se pierde — sigue en el
                // detalle, con su demora. Ver la cabecera de `seguimientoTraspaso.js`.
                if (!traspasoVisibleEnLista(chat)) return null;
                const estadoTraspaso = estadoSeguimientoTraspaso(chat);
                const pendiente = estadoTraspaso === 'pendiente';
                const espera = esperaDeTraspaso(chat);
                const horas = Number.isFinite(espera) ? Math.floor(espera / 3600000) : null;
                return (
                  <MarcaDeChat
                    icono={pendiente ? '🙋' : '✅'}
                    texto={pendiente ? 'Espera a una persona' : 'Traspaso atendido'}
                    sufijo={pendiente && horas !== null && horas >= 1 ? `· ${horas} h` : null}
                    fondo={pendiente ? '#feebc8' : '#e6fffa'}
                    color={pendiente ? '#7b341e' : '#234e52'}
                    titulo={motivoDeTraspaso(chat) || undefined}
                  />
                );
              })()}

              {/* Marca de COTIZACIÓN ENTREGADA (F, 3-nov-2026): la escribe el webhook de estados al confirmar la
                  entrega de un archivo marcado como cotización; «sin respuesta» hasta que el cliente escribe. */}
              {tieneCotizacionEnviada(chat) && (
                <MarcaDeChat
                  icono="📄"
                  texto={t('cotizacion.marca')}
                  sufijo={`· ${fechaDeCotizacion(chat) || ''}${cotizacionSinRespuesta(chat) ? ` · ${t('cotizacion.sinRespuesta')}` : ''}`}
                  fondo="#ebf8ff"
                  color="#2a4365"
                  titulo={chat.cotizacionEnviada?.nombreArchivo || chat.cotizacionEnviada?.clave || undefined}
                />
              )}

              {/* Header: Client name + Timestamp */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: '0.75rem'
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <h3 style={{
                    margin: 0,
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#1a202c',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {chat.nombre || chat.remitente || t('list.noName')}
                  </h3>
                  <p style={{
                    margin: '2px 0 0 0',
                    fontSize: '12px',
                    color: '#718096',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {chat.numero || chat.numeroId || chat.waId || t('list.noNumber')}
                  </p>
                </div>
                <span style={{
                  fontSize: '12px',
                  color: '#a0aec0',
                  fontWeight: '500',
                  marginLeft: '1rem',
                  flexShrink: 0
                }}>
                  {formatRelativeTime(chat.actualizadoEn)}
                </span>
              </div>

              {/* Channel + Status Badges */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                marginBottom: '0.75rem',
                flexWrap: 'wrap'
              }}>
                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.25rem',
                  backgroundColor: channelConfig.light,
                  color: channelConfig.dark,
                  padding: '0.25rem 0.5rem',
                  borderRadius: '12px',
                  fontSize: '11px',
                  fontWeight: '600',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  <span style={{ fontSize: '14px' }}>{channelConfig.icon}</span>
                  {channelConfig.label}
                </span>

                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.25rem',
                  backgroundColor: `${statusConfig.color}15`,
                  color: statusConfig.color,
                  padding: '0.25rem 0.5rem',
                  borderRadius: '12px',
                  fontSize: '11px',
                  fontWeight: '600'
                }}>
                  <span>{statusConfig.icon}</span>
                  {statusConfig.label}
                </span>
              </div>

              {/* Message Preview */}
              <p style={{
                margin: 0,
                fontSize: '14px',
                color: '#4a5568',
                lineHeight: '1.5',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                marginBottom: '0.75rem'
              }}>
                {(chat.ultimoMensajeTexto || chat.ultimoMensaje || '').trim() || t('list.noMessages')}
              </p>

              {/* SIN LEER + PAUSADO.
                  Van juntos y en la misma fila a propósito: la combinación de los dos
                  es la que importa. Desde que la reanudación automática se retiró, una
                  conversación pausada NO vuelve sola — así que un chat pausado con
                  mensajes esperando es exactamente el que se puede quedar olvidado, y
                  la única protección que queda es que se VEA. Por eso ahí el aviso pasa
                  a rojo y dice las dos cosas en un solo distintivo, en vez de dejar dos
                  pastillas suaves que se leen por separado. */}
              {/* BLOQUEADO. Va el primero y en rojo sólido: es el estado más fuerte que
                  puede tener una conversación —el bot no responde y, si además hay
                  bloqueo aguas arriba, la persona ni siquiera puede escribir—. Si se
                  mezclara con los demás distintivos, un chat bloqueado se leería como
                  uno pausado, que es reversible y significa otra cosa. */}
              {chat.contactoBloqueado === true && (
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.3rem',
                  marginBottom: '0.5rem',
                  padding: '0.25rem 0.6rem',
                  borderRadius: '999px',
                  background: admiteBloqueoReal(chat) ? '#c53030' : '#dd6b20',
                  color: '#fff',
                  fontSize: '11px',
                  fontWeight: 700,
                }}>
                  {/* Rojo = bloqueado de verdad (no puede escribir).
                      Ámbar = sólo silenciado (sigue escribiendo, el bot calla). */}
                  {admiteBloqueoReal(chat) ? '🚫 ' : '🔇 '}
                  {t(`block.badge${admiteBloqueoReal(chat) ? '' : 'Silenced'}`)}
                </div>
              )}

              {/* La fila de insignias: se pinta si hay alguna que enseñar. */}
              {(esperandoRespuesta(chat) || chat.botPaused) && (
                <div style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '0.35rem',
                  marginBottom: '0.5rem',
                }}>
                  {/* SIN RESPONDER · el cliente habló el último y nadie le contestó.
                      Los días y no un número de mensajes: «3 sin leer» no es accionable y
                      «14 días» sí. Un chat PAUSADO no lleva marca — lo decide la función
                      pura, no este render. */}
                  {esperandoRespuesta(chat) && (() => {
                    const dias = diasEsperando(chat);
                    return (
                      <span data-testid="sin-responder" style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.3rem',
                        background: dias >= 1 ? '#fff3cd' : '#e6f4ea',
                        color: dias >= 1 ? '#7a5b00' : '#1e7e34',
                        padding: '0.2rem 0.55rem',
                        borderRadius: '10px',
                        fontSize: '11px',
                        fontWeight: 700,
                      }}>
                        ⏳{' '}
                        {dias >= 1
                          ? t('list.awaitingReplyDays', { count: dias })
                          : t('list.awaitingReply')}
                        {/* El detector de bucle pausó el bot: puede ser otro bot o una persona
                            que escribe muy rápido y largo. Se dice, para que no quede ignorado. */}
                        {motivoDeEspera(chat) === 'posible-bot' && ` · ${t('list.awaitingReplyPossibleBot')}`}
                        {motivoDeEspera(chat) === 'tope-plan' && ` · ${t('list.awaitingReplyPlanLimit')}`}
                        {motivoDeEspera(chat) === 'cuenta-pux' && ` · ${t('list.awaitingReplyPuxAccount')}`}
                        {motivoDeEspera(chat) === 'app-business' && ` · ${t('list.awaitingReplyAppBusiness')}`}
                      </span>
                    );
                  })()}
                  {chat.botPaused && (
                    <span style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.25rem',
                      background: '#fff3cd',
                      color: '#856404',
                      padding: '0.2rem 0.5rem',
                      borderRadius: '10px',
                      fontSize: '11px',
                      fontWeight: 600,
                    }}>
                      {/* ⚠️ UNA SOLA VARIANTE. Aquí había un «Pausado y esperando
                          respuesta» cuando el contador era > 0, y se retiró con él: un
                          chat pausado NO se marca como esperando —decisión del
                          24-sep-2026, con los 2 pausados medidos y uno de ellos ya
                          bloqueado—. Dejar esa variante sería marcarlos por otra puerta. */}
                      ⏸️ {t('detail.bot.pausedBadge')}
                    </span>
                  )}
                </div>
              )}

              {/* Action Buttons */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                paddingTop: '0.75rem',
                borderTop: '1px solid #e2e8f0',
                flexWrap: 'wrap',
              }}>
                <select
                  value={chatEstado}
                  onChange={(e) => {
                    e.stopPropagation();
                    handleEstadoChange(chat.id, e.target.value);
                  }}
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    padding: '0.4rem 0.6rem',
                    fontSize: '13px',
                    border: '1px solid #cbd5e0',
                    borderRadius: '6px',
                    backgroundColor: '#fff',
                    color: '#4a5568',
                    cursor: 'pointer',
                    flex: 1
                  }}
                >
                  {ESTADOS_DE_CHAT.map(({ codigo }) => (
                    <option key={codigo} value={codigo}>{etiquetaDeEstado(codigo)}</option>
                  ))}
                </select>

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleBotPauseChat(chat.id, chat.botPaused === true);
                  }}
                  title={chat.botPaused ? t('detail.bot.resume') : t('detail.bot.pause')}
                  style={{
                    padding: '0.4rem 0.6rem',
                    fontSize: '13px',
                    border: '1px solid',
                    borderColor: chat.botPaused ? '#28a745' : '#ffc107',
                    borderRadius: '6px',
                    background: chat.botPaused ? '#d4edda' : '#fff3cd',
                    color: chat.botPaused ? '#155724' : '#856404',
                    cursor: 'pointer',
                    flexShrink: 0,
                    fontWeight: 600,
                  }}
                  aria-label={chat.botPaused ? t('detail.bot.resume') : t('detail.bot.pause')}
                >
                  {chat.botPaused ? '▶️' : '⏸️'}
                </button>

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    irAlChat(chat);
                  }}
                  style={{
                    ...styles.viewButton,
                    flex: 1,
                    padding: '0.5rem 1rem',
                    fontSize: '13px'
                  }}
                >
                  {t('list.viewConversation')}
                </button>
              </div>

              {estadoNotif[chat.id] && (
                <div style={{
                  marginTop: '0.5rem',
                  fontSize: '12px',
                  color: '#718096',
                  fontStyle: 'italic'
                }}>
                  {estadoNotif[chat.id]}
                </div>
              )}
            </div>
            );
          })}
        </div>
      )}

      {/* ✨ Fase 5: Improved Loading Indicator */}
      {loading && chats.length > 0 && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.5rem',
          padding: '1rem',
          color: '#6c757d'
        }}>
          <div style={{
            display: 'inline-block',
            animation: 'spin 1s linear infinite',
            fontSize: '20px'
          }}>
            🔄
          </div>
          <span>{t('list.loadingMore')}</span>
        </div>
      )}
      {/* EL AVISO DEL TOPE. Sólo aparece cuando el agotado al filtrar chocó con
          TOPE_CARGA_AL_FILTRAR, o sea cuando el filtro NO está mirando todo. Callar
          aquí sería enseñar una lista incompleta con pinta de completa — el mismo
          fallo que esta pieza vino a arreglar, un piso más arriba. */}
      {/* ⚠️ EL OYENTE CAÍDO SE DICE, NO SE CALLA. Si falla —permisos al cambiar de
          organización, red— la lista NO se vacía: se queda lo último conocido. Pero
          quedarse con datos viejos SIN avisar es peor que quedarse en blanco, porque el
          operador no tiene forma de saber que está mirando una foto. */}
      {oyenteCaido && (
        <div style={{
          padding: '0.6rem 0.9rem', marginBottom: '0.8rem', borderRadius: '8px',
          background: '#fffaf0', border: '1px solid #f6ad55', color: '#7b341e',
          fontSize: '13px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.8rem',
        }}>
          <span>⚠️ La lista puede no estar al día: se perdió la conexión con el servidor.</span>
          <button
            onClick={() => window.location.reload()}
            style={{ border: '1px solid #f6ad55', background: 'transparent', color: '#7b341e',
                     borderRadius: '6px', padding: '0.25rem 0.6rem', cursor: 'pointer', fontSize: '12px' }}
          >Reintentar</button>
        </div>
      )}
      {topeAlcanzado && (
        <div style={{
          margin: '0 0 1rem',
          padding: '0.75rem 1rem',
          borderRadius: '8px',
          background: '#fff3cd',
          borderLeft: '4px solid #ffc107',
          color: '#856404',
          fontSize: '13px'
        }}>
          ⚠️ {t('list.filterCapReached', { count: TOPE_CARGA_AL_FILTRAR })}
        </div>
      )}

      {hasMore && <div ref={loaderRef} style={{ height: '50px' }} />}

      {/* ✨ Fase 5: CSS Animations */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

const styles = {
  chatCard: {
    display: 'flex',
    flexDirection: 'column',
    padding: '1rem',
    border: '1px solid #ddd',
    borderRadius: '8px',
    backgroundColor: '#fff',
    boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
  },
  viewButton: {
    backgroundColor: '#1a73e8',
    color: '#fff',
    border: 'none',
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    cursor: 'pointer'
  },
  exportBtn: {
    backgroundColor: '#28a745',
    color: '#fff',
    border: 'none',
    padding: '0.3rem 0.8rem',
    borderRadius: '6px',
    cursor: 'pointer'
  }
};

export default Chats;